<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useClassStore, useLoadingStore, useSessionStore, useUserStore } from '../../../stores';
import { Session, VocabRecordType, VocabResult, VocabResultDecision } from '../../../types/session';
import AudioPlayer from '../../General/AudioPlayer.vue';
import AllowSpeaking from './AllowSpeaking.vue';
import { useI18n } from 'petite-vue-i18n';
import { TEXT } from '../../../configs/text';
import { UserRole } from '../../../types/user';
import AudioRecorder from './AudioRecorder.vue';
import VocabResultDetails from './VocabResultDetails.vue';
import VocabResultRecorded from './VocabResultRecorded.vue';
import Button from '../../General/Button.vue';
import Slide from '../../Animation/Slide.vue';
import ImageContainer from '../../General/ImageContainer.vue';

const loading = useLoadingStore()
const user = useUserStore()
const classStore = useClassStore()
const sessionStore = useSessionStore()
const vocabItem = computed(() => {
    return sessionStore.classContent?.[Session.vocab]?.vocabItems?.find(item => item.id === sessionStore.currentVocabId)
})

const blob = ref<Blob | null>(null)
const vocabRecordAttempts = computed<VocabResult[]>(() => sessionStore.vocabRecordAttempts?.[classStore.roomName]?.[user.username || '']?.[sessionStore.currentVocabId]?.[sessionStore.vocabRecordingType] || [])
const currentAttemptIndex = ref<number | null>(resetAttemptIndex())
const maxAttempt = 3

const isAllowRecord = computed<boolean>(() => {
    if (user.userRole === UserRole.teacher) return true;
    if (user.userRole === UserRole.student && user.userInfo?.id && sessionStore.allowSpeakingStudentIds.includes(user.userInfo.id)) return true;
    return false;
})

//record results use star layout or table
const isStarLayout = computed(() => !!classStore.selectedClassDetails?.grade && classStore.selectedClassDetails.grade <= 5)

const isRecordable = computed<boolean>(() => sessionStore.recordAttemptCount < maxAttempt)
const isSubmitted = ref<boolean>(false)

function handleSelectRecordAttempt(index: number) {
    if (!vocabRecordAttempts.value[index]) return;
    currentAttemptIndex.value = index
}

function resetAttemptIndex(): number | null {
    return vocabRecordAttempts.value.length > 0 ? vocabRecordAttempts.value.length - 1 : null
}

function highlightBrackets(text: string): string {
    const regex = /\[\[(.+?)\]\]/g
    return text.replace(regex, (_match, p1) => {
        return `<span class="text-yellow-500 font-semibold">${p1}</span>`
    })
}

function removeBrackets(text: string): string {
    const re = /\[\[(.+?)\]\]/g
    return text.replace(re, (_match, p1) => p1)
}

async function handleRecordingSubmit() {
    if (!blob.value) return;
    if (!vocabItem.value?.word) return;

    loading.isLoading = true
    await sessionStore.submitAudioVocab(sessionStore.vocabRecordingType === VocabRecordType.word ? vocabItem.value.word : removeBrackets(vocabItem.value.contentExample), blob.value, sessionStore.vocabRecordingType, vocabItem.value.id)
    isSubmitted.value = true
    currentAttemptIndex.value = resetAttemptIndex()
}

watch(blob, () => {
    if (blob.value) return;
    isSubmitted.value = false
})

watch(() => sessionStore.currentVocabId, () => {
    blob.value = null
    currentAttemptIndex.value = resetAttemptIndex()
})

watch(() => sessionStore.vocabRecordingType, () => {
    blob.value = null
    currentAttemptIndex.value = resetAttemptIndex()
})

onBeforeUnmount(() => {
    blob.value = null
    currentAttemptIndex.value = null
})

function getTextColor(decision: VocabResultDecision): 'main' | 'secondary' | 'success' | 'info' | 'danger' | 'warning' | 'gray' | 'light' | 'white' {
    switch (decision) {
        case VocabResultDecision.correct:
            return 'success';
        case VocabResultDecision.warning:
            return 'warning';
        case VocabResultDecision.incorrect:
            return 'danger';
        default:
            return 'light';
    }
}

const { t } = useI18n()
</script>

<template>
    <div class="relative flex flex-col gap-3 h-full w-full">
        <div class="flex-1 flex flex-col items-center overflow-y-auto w-full overflow-x-hidden" v-if="vocabItem?.id">
            <!-- image -->
            <ImageContainer v-if="vocabItem?.illustrationUrl" :alt="vocabItem?.word" :src="vocabItem.illustrationUrl" class="h-[40%] mt-3" imgClass="max-h-full"></ImageContainer>

            <div class="w-full grid grid-cols-1 gap-5 mt-3 p-3" :class="{ 'md:grid-cols-2': vocabItem?.contentExample }">
                <!-- word -->
                <div class="flex w-full" @click="sessionStore.vocabRecordingType = VocabRecordType.word">
                    <div class=" flex flex-1 flex-col gap-3 items-center justify-center text-center border border-neutral-200 hover:border-neutral-100 border-b-4 lg:border-b-8 rounded-s-xl p-3 bg-neutral-100 hover:bg-white transition-all cursor-pointer"
                        :class="[{ 'bg-white border-neutral-100': sessionStore.vocabRecordingType == VocabRecordType.word },
                        vocabItem?.vocabVoiceUrl ? 'border-e-0' : 'rounded-e-xl'
                        ]">
                        <span class="text-yellow-500 text-2xl lg:text-3xl font-bold">{{ vocabItem?.word }}</span>
                        <span class="lg:text-lg text-neutral-500">{{ vocabItem?.word_phonetic }}</span>
                    </div>
                    <AudioPlayer v-if="vocabItem?.vocabVoiceUrl" :src="vocabItem.vocabVoiceUrl">
                        <template #button="{ icon, isLoading }">
                            <div class="flex items-center justify-center cursor-pointer w-10 shrink-0 border border-s-0 border-sky-500 hover:border-sky-600 border-b-4 lg:border-b-8 rounded-s-none rounded-e-xl bg-sky-400 hover:bg-sky-500">
                                <component :is="icon" class="size-5 fill-white pointer-events-none" :class="{ 'animate-spin': isLoading }" />
                            </div>
                        </template>
                    </AudioPlayer>
                </div>

                <!-- example -->
                <div class="flex flex-1 w-full" @click="sessionStore.vocabRecordingType = VocabRecordType.example" v-if="vocabItem?.contentExample">
                    <div class=" flex flex-1 flex-col gap-3 items-center justify-center text-center border border-neutral-200  rounded-s-xl hover:border-neutral-100 border-b-4 lg:border-b-8 p-3 bg-neutral-100 hover:bg-white transition-all cursor-pointer"
                        :class="[{ 'bg-white border-neutral-100': sessionStore.vocabRecordingType == VocabRecordType.example },
                        vocabItem?.exampleVoiceUrl ? 'border-e-0' : 'rounded-e-xl'
                        ]">
                        <div class="lg:text-lg font-bold" v-html="highlightBrackets(vocabItem.contentExample)"></div>
                    </div>
                    <AudioPlayer v-if="vocabItem?.exampleVoiceUrl" :src="vocabItem.exampleVoiceUrl">
                        <template #button="{ icon, isLoading }">
                            <div class="flex items-center justify-center cursor-pointer w-10 shrink-0 border border-s-0 border-sky-500 hover:border-sky-600 border-b-4 lg:border-b-8 rounded-s-none rounded-e-xl bg-sky-400 hover:bg-sky-500">
                                <component :is="icon" class="size-5 fill-white pointer-events-none" :class="{ 'animate-spin': isLoading }" />
                            </div>
                        </template>
                    </AudioPlayer>
                </div>

                <!-- record word -->
                <div class="flex flex-col w-full">
                    <div class="flex flex-col items-center w-full gap-3" v-if="sessionStore.vocabRecordingType === VocabRecordType.word">
                        <template v-if="isAllowRecord">
                            <AudioRecorder v-model="blob" :key="`${vocabItem.id}_${sessionStore.vocabRecordingType}`" :disabled="!isRecordable" :submitted="isSubmitted" @submit="handleRecordingSubmit" class="mt-6" />
                            <div class="text-center mt-2">
                                <div class="text-sm">
                                    {{ t(TEXT.attempts_remaining, {
                                        current_attempt: maxAttempt - sessionStore.recordAttemptCount,
                                        max_attempt: maxAttempt
                                    }) }}
                                </div>
                            </div>
                        </template>

                        <div class="flex items-start justify-center gap-2 my-3" v-if="currentAttemptIndex !== null">
                            <div class="flex flex-col items-center gap-2" v-for="(attempt, index) in maxAttempt">
                                <Button :border="false" class="!p-2 size-10 !text-black" :color="index <= vocabRecordAttempts.length - 1 ? getTextColor(vocabRecordAttempts[index].decision) : 'light'" @click="handleSelectRecordAttempt(index)">
                                    {{ attempt }}
                                </Button>
                                <Slide direction="up-reverse" :key="index">
                                    <div class=" w-4/5 h-[3px] rounded-full bg-sky-500" v-if="index === currentAttemptIndex"></div>
                                </Slide>
                            </div>
                        </div>

                        <VocabResultRecorded v-if="currentAttemptIndex !== null" :result="vocabRecordAttempts[currentAttemptIndex]" :key="`${vocabItem.id}${currentAttemptIndex}`" />

                        <VocabResultDetails class=" mt-3" v-if="currentAttemptIndex !== null && isStarLayout" type="stars" :result="vocabRecordAttempts[currentAttemptIndex]" :key="`${vocabItem.id}${currentAttemptIndex}`" />
                    </div>
                </div>

                <!-- record example -->
                <div class="flex flex-col w-full" v-if="vocabItem?.contentExample">
                    <div class="flex flex-col items-center w-full gap-3" v-if="sessionStore.vocabRecordingType === VocabRecordType.example">
                        <template v-if="isAllowRecord">
                            <AudioRecorder v-model="blob" :key="`${vocabItem.id}_${sessionStore.vocabRecordingType}`" :disabled="!isRecordable" :submitted="isSubmitted" @submit="handleRecordingSubmit" class="mt-6" />
                            <div class="text-center mt-2">
                                <div class="text-sm">
                                    {{ t(TEXT.attempts_remaining, {
                                        current_attempt: maxAttempt - sessionStore.recordAttemptCount,
                                        max_attempt: maxAttempt
                                    }) }}
                                </div>
                            </div>
                        </template>

                        <div class="flex items-start justify-center gap-2 my-3" v-if="currentAttemptIndex !== null">
                            <div class="flex flex-col items-center gap-2" v-for="(attempt, index) in maxAttempt">
                                <Button :border="false" class="!p-2 size-10 !text-black" :color="index <= vocabRecordAttempts.length - 1 ? getTextColor(vocabRecordAttempts[index].decision) : 'light'" @click="handleSelectRecordAttempt(index)">
                                    {{ attempt }}
                                </Button>
                                <Slide direction="up-reverse" :key="index">
                                    <div class=" w-4/5 h-[3px] rounded-full bg-sky-500" v-if="index === currentAttemptIndex"></div>
                                </Slide>
                            </div>
                        </div>

                        <VocabResultRecorded v-if="currentAttemptIndex !== null" :result="vocabRecordAttempts[currentAttemptIndex]" :key="`${vocabItem.id}${currentAttemptIndex}`" />

                        <VocabResultDetails v-if="currentAttemptIndex !== null && isStarLayout" type="stars" :result="vocabRecordAttempts[currentAttemptIndex]" :key="`${vocabItem.id}${currentAttemptIndex}`" />
                    </div>
                </div>

                <div class="flex flex-col" :class="{ 'md:col-span-2': vocabItem?.contentExample }">
                    <VocabResultDetails v-if="currentAttemptIndex !== null && !isStarLayout" type="table" :result="vocabRecordAttempts[currentAttemptIndex]" :key="`${vocabItem.id}${currentAttemptIndex}`" />
                </div>
            </div>
        </div>
        <AllowSpeaking v-if="user.userRole === UserRole.teacher" />
    </div>
</template>